/**
 * rateTeam.js — grade ONE hand-entered team on /rate's "Rate my team" tab.
 *
 * Runs entirely CLIENT-SIDE from two committed artifacts and nothing else:
 *   - src/data/rateBenchmarks.json   per-player market medians + slot ladders
 *   - src/data/rateTeamDists.json    per-team corpus distributions (the yardstick)
 * No API, no fetch, no storage. CRA's module scope forbids importing from api/,
 * so the pick math below is a deliberate mirror of api/_lib/rateReport.js's
 * `graded` block rather than a call into it, and rateTeam.test.js pins the two
 * together against a real harvest team so the copy cannot drift unnoticed.
 *
 * WHY A SEPARATE MODULE AT ALL: the league report grades a team against ITS OWN
 * ROOM. A pasted single team has no room, so every room-relative number needs a
 * corpus yardstick instead — the letter grade, the position chips and the "beats
 * X%" line all read off rateTeamDists rather than off eleven neighbours.
 *
 * 🔴 THE ONE PLACE THE MATH CANNOT BE IDENTICAL, AND WHAT WE DO ABOUT IT.
 * computeReport ranks a pick against every sale at that position IN THE ROOM
 * ("the rank the player went in THIS room"), then reads the slot ladder at that
 * rank. A lone team has no room to rank inside. Ranking within the entered team
 * is available but wrong: a room's 25th RB is his own team's 3rd, which would
 * grade a $4 bench back against the ladder's 3rd rung.
 *
 * So the rank here is the one the LADDER ITSELF implies: how many rungs sit
 * strictly above what was paid, i.e. "in a room paying market, a player at this
 * price is about the k-th most expensive at his position". Measured against the
 * whole harvest corpus (1358 teams, 21092 picks, Aug 30 2026):
 *   - only 12 picks in 21092 (0.1%) are slot-sourced at all; the player table
 *     and the <=$3 tail rule between them cover everything else, which is why
 *     this seam is narrow enough to be worth a mirror in the first place
 *   - the ladder-implied rank reproduces computeReport's team surplus EXACTLY
 *     on 1348 of 1358 teams, and the largest disagreement on the rest is $8
 *     (a within-team rank matches equally often but misses by up to $85)
 * Do not "simplify" this to a within-team rank. It looks more natural and is
 * measurably worse.
 *
 * 🔴 TWO LENSES, AND WHICH ONE APPLIES IS NOT A DETAIL (Aug 30 2026).
 * The team is projected TWICE:
 *   DISPLAY   under the user's own scoring. Everything visible is this one -
 *             the headline points, PPG, the season column, position ranks, and
 *             which players the lineup filler started.
 *   REFERENCE under REFERENCE_LENS (half PPR, 4pt pass TD), the same lens
 *             rateTeamDists was built with. EVERY percentile and every position
 *             word comes from this one, on both sides of the comparison.
 * A percentile whose two sides are scored by different rules is not a
 * comparison. Ben's Standard 6pt league is the case that forced this: his
 * quarterbacks scored ~30% more than a 4pt room's for identical play, so the
 * old percentile flattered him for his league's settings rather than for his
 * roster.
 *
 * The two lineups can DISAGREE about who plays flex, and that is correct: the
 * rows show the lineup he will actually start, the score compares the lineup
 * the corpus was measured on. Surplus and the value percentile are untouched -
 * they are dollars, and dollars have no lens.
 */

import benchmarks from "../data/rateBenchmarks.json";
import { REFERENCE_LENS } from "../data/rateLenses.js";
import dists from "../data/rateTeamDists.json";
import { normName } from "./playerNameNorm.js";
import { scoreFromStats } from "./projlabProjection.js";
import {
  FLEX_KINDS,
  GRADE_POSITIONS,
  GRADE_WORDS,
  GROUP_ORDER,
  KD_POSITIONS,
  flexScore,
  kdIndex,
  playerKey,
  positionScore,
  projGroup,
  projectTeam,
  projectedSlotCount,
  projectionIndex,
  splitRoster,
} from "./rateProjection.js";

/** Positions the benchmark tables carry. K/DEF always grade as surplus 0. */
const LADDER_POS = new Set(["QB", "RB", "WR", "TE"]);

/** Same rungs, same order as api/_lib/rateReport.js. */
const GRADE_RUNGS = ["A+", "A", "A-", "B+", "B", "B-", "C+", "C", "C-", "D"];

/** Rule-1 label, verbatim from the league report. Both tabs say the same thing. */
export const BENCHMARK_LABEL =
  "graded against median prices from real 2026 Sleeper auctions, scaled to your budget";

const SCALE_BUDGET = 200;

function round1(v) {
  return Math.round(v * 10) / 10;
}

/**
 * Format from the league's own settings counts — HOUSE keys (SFLEX), not
 * Sleeper strings (SUPER_FLEX). Same three-way split and the same two notes as
 * the league report: a SFLEX slot means superflex; 2+ QB slots grade on the
 * superflex cell as the closest one we have; everything else is 1QB.
 */
export function pickFormat(positions) {
  const p = positions || {};
  if ((Number(p.SFLEX) || 0) > 0) return { key: "superflex", label: "superflex" };
  if ((Number(p.QB) || 0) >= 2) return { key: "superflex", label: "2qb" };
  return { key: "1qb", label: "1qb" };
}

/**
 * House position counts -> the slot shape rateProjection's lineup filler reads.
 *
 * The only rename is SFLEX -> SUPER_FLEX. BN is dropped because a bench slot is
 * not a lineup slot.
 *
 * 🔴 DEF AND K NOW COME THROUGH (Aug 30 2026). They used to be dropped here
 * because nothing projected them, so a kicker slot could only ever have been
 * filled with a blank. Now that kdProjections2026.js exists, dropping them
 * would leave two real lineup slots empty and understate every roster that
 * starts them - which is the exact defect Ben reported ("the point projections
 * are off if you don't").
 */
export function projectionSlots(positions) {
  const p = positions || {};
  const out = {};
  for (const key of ["QB", "RB", "WR", "TE", "DEF", "K", "FLEX"]) {
    const n = Number(p[key]) || 0;
    if (n > 0) out[key] = n;
  }
  const sflex = Number(p.SFLEX) || 0;
  if (sflex > 0) out.SUPER_FLEX = sflex;
  return out;
}

/**
 * Where `value` sits in an ascending array, as the percentage of entries it
 * strictly beats. Read literally by the copy ("beats X% of N real teams"), so
 * ties deliberately do NOT count as beaten: matching the best team in the
 * corpus is not beating it.
 *
 * Below the minimum -> 0. Above the maximum -> 100. Empty array -> null, which
 * the caller renders as no claim rather than as 0.
 */
export function percentileOf(sorted, value) {
  const arr = sorted || [];
  if (arr.length === 0 || !Number.isFinite(value)) return null;
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid] < value) lo = mid + 1;
    else hi = mid;
  }
  return (lo / arr.length) * 100;
}

/**
 * Percentile -> letter, best-first over the same ten rungs the room report
 * uses. 100th percentile is A+, 0th is D. The room report maps a RANK within
 * twelve teams; this maps a position within ~1000 real ones, which is why the
 * copy on the team tab says what the grade is measured against.
 */
export function gradeFromPercentile(pct) {
  if (!Number.isFinite(pct)) return null;
  const clamped = Math.max(0, Math.min(100, pct));
  const i = Math.round(((100 - clamped) / 100) * (GRADE_RUNGS.length - 1));
  return GRADE_RUNGS[i];
}

/**
 * The ROOM's own letter grades: rank on total surplus, mapped onto the same ten
 * rungs, exactly as api/_lib/rateReport.js grades a fetched auction.
 *
 * 🔴 A DELIBERATE MIRROR, LIKE THE PICK MATH ABOVE IT. CRA's module scope
 * forbids importing from api/, and this is the formula the league report has
 * always used: sort by surplus, spread the rungs evenly across the field. Auction
 * money is near zero-sum, so the room IS the curve and nobody gets an F.
 *
 * 🔴 ONLY EVER CALLED ON A FULL ROOM. Grading three of ten entered teams against
 * each other would rank the teams somebody happened to type in and call it a
 * league. The caller owns that rule; this function owns the formula.
 *
 * @param surpluses per-team total surplus, index aligned with the caller's list
 * @returns the same length: { rank, grade } per team, null where there is no
 *          surplus to rank
 */
export function roomGradesBySurplus(surpluses) {
  const list = surpluses || [];
  const order = list
    .map((v, i) => [i, Number.isFinite(v) ? v : null])
    .filter(([, v]) => v !== null)
    .sort((a, b) => b[1] - a[1]);
  const denom = Math.max(order.length - 1, 1);
  const out = list.map(() => null);
  order.forEach(([i], k) => {
    out[i] = {
      rank: k + 1,
      grade: GRADE_RUNGS[Math.round((k / denom) * (GRADE_RUNGS.length - 1))],
    };
  });
  return out;
}

/**
 * Tercile word for one positional depth score, against THE CORPUS for that
 * position and format — not against the other positions on this roster, and
 * not against a room. Same three words and the same best-first order as the
 * league report's chips, so the two tabs cannot say different things about the
 * same roster.
 */
export function positionWord(score, sortedPos) {
  const pct = percentileOf(sortedPos, score);
  if (pct === null) return null;
  const i = Math.min(GRADE_WORDS.length - 1, Math.floor(((100 - pct) / 100) * GRADE_WORDS.length));
  return GRADE_WORDS[i];
}

/**
 * Which week1 population this team is measured against: the exact
 * `teams-slots` bucket, else a cell of its own SIZE, else nothing.
 *
 * 🔴 THERE IS NO POOLED LEVEL, AND ITS REMOVAL IS A CORRECTION. This function
 * used to end in `dist.week1`, written before the size effect had been
 * measured. It had not been a fallback so much as a wrong answer with a
 * caption: a 10-team room starts fewer lineups from the same player pool, so
 * its week 1 totals sit higher, and the pooled 1QB cell is 442/582 twelve-team
 * rosters. It reached further than superflex-14, too — `teamMix` carries only
 * 10, 12 and 14, so every 8- and 16-team league was being placed against it,
 * and 8 sits further off than 10 did.
 *
 * So week1, surplus and the position chips now answer the same way: a report
 * either has a population of this league's size or it has none. `matchedOn` is
 * "size" for the exact teams-slots bucket and "teams" for a same-size cell not
 * split by starter count; there is no third value, because there is no third
 * population. `exact` is kept as `matchedOn === "size"` so existing callers
 * keep their meaning.
 *
 * The middle level is a REAL population of this league's size, simply not cut
 * by starter count. That confound is genuine and much smaller than the one it
 * replaces: mixing a 9-slot and a 10-slot lineup within 10-team rooms moves
 * week1 by far less than comparing a 10-team roster against mostly 12-team
 * ones.
 *
 * `report.week1` itself is untouched by any of this. The projected points
 * total is a fact about the roster; only the percentile and the meter that
 * draws it are comparisons.
 *
 * @returns { values, n, exact, matchedOn, teams, slots } or null when the
 *   corpus has no cell for this league size.
 */
export function resolveWeek1Cell(dist, teams, slots) {
  const bucket = (dist.week1Buckets || {})[`${teams}-${slots}`] || null;
  if (bucket) {
    return { values: bucket, n: bucket.length, exact: true, matchedOn: "size", teams, slots };
  }
  const bySize = (dist.week1ByTeams || {})[String(teams)] || null;
  if (bySize) {
    return { values: bySize, n: bySize.length, exact: false, matchedOn: "teams", teams, slots };
  }
  return null;
}

/**
 * Which season population this team's projected season total is measured
 * against: this league's own size, or nothing.
 *
 * 🔴 SIZE ONLY, AND THERE IS NO SHAPE BUCKET TO PREFER. week1 has a
 * `teams-slots` cell because it was cut that way from the start and those cells
 * cleared the artifact's floor; `seasonByTeams` ships the size cut alone, so
 * this resolves one way and `matchedOn` is always "teams". The confound that
 * leaves — a 9-slot and a 10-slot lineup inside one room size — is the same one
 * resolveWeek1Cell's middle level already tolerates and names.
 *
 * 🔴 AND NO POOLED LEVEL, for the reason every other cell here has none: a
 * season total moves with room size because fewer seats split the same player
 * pool, so a pooled percentile would read a 10-team roster as strong for being
 * ten-handed. A size the corpus cannot cover gets no season percentile and the
 * report says so; the season TOTAL is a fact about the roster and is shown
 * either way.
 *
 * @returns { values, n, matchedOn: "teams", teams } or null.
 */
export function resolveSeasonCell(dist, teams) {
  const cell = (dist.seasonByTeams || {})[String(teams)] || null;
  if (!cell) return null;
  return { values: cell, n: cell.length, matchedOn: "teams", teams };
}

/**
 * Which surplus population this team's market grade is measured against.
 *
 * 🔴 THERE IS NO POOLED FALLBACK HERE, DELIBERATELY. Surplus moves with room
 * size harder than week 1 points do: on this corpus the median team's
 * $200-scaled surplus runs +29.60 at 10 teams, -5.35 at 12 and -33.00 at 14
 * (1QB; superflex +30.75 / -5.30 / -32.16), because fewer teams means less
 * money chasing the same players. Graded against the pooled curve the median
 * 10-team team sits at the 93.5th percentile and gradeFromPercentile hands it
 * an A — a whole room of A's for being a 10-team room. The same defect pointed
 * the other way makes superflex-14 a room of D's.
 *
 * So a size the corpus cannot cover gets NO corpus market grade rather than a
 * pooled one. The pooled number is not a weaker answer, it is the wrong answer,
 * and the report says there is not enough data at this size yet. The
 * room-relative grade (roomGradesBySurplus) is unaffected and stays valid at
 * every size — it compares seats within one room, where size cancels.
 *
 * @returns { values, n, matchedOn: "teams" } or null when the corpus has no
 *   cell for this league size.
 */
export function resolveSurplusCell(dist, teams) {
  const cell = (dist.surplusByTeams || {})[String(teams)] || null;
  if (!cell) return null;
  return { values: cell, n: cell.length, matchedOn: "teams", teams };
}

/**
 * Which positional-depth population one chip is measured against: this
 * league's own size, or nothing.
 *
 * 🔴 THE SAME CONFOUND AS week1, FOR THE SAME REASON. Positional depth is the
 * sum of a team's best projected players at a position, so a 10-team roster's
 * RB pair is stronger for exactly the reason its week 1 total is: fewer seats
 * split the same player pool. Graded against the pooled per-position arrays a
 * 10-team room's chips read strong across the board, which is the league's size
 * showing up as six separate compliments about the roster.
 *
 * Cut per CELL rather than per size, matching the artifact: a size keeps the
 * groups that clear the floor and drops the ones that do not, independently. A
 * missing cell is null, never the pooled array — the loop already renders a
 * null percentile for the "nothing matched" case, so there is one path for
 * "no answer" rather than two.
 */
export function resolvePosCell(dist, teams, group) {
  const bySize = (dist.posByTeams || {})[String(teams)] || null;
  return (bySize && bySize[group]) || null;
}

/**
 * The slot ladder rank a lone price implies: the count of rungs strictly above
 * it. See the header note for why this rather than a within-team rank.
 */
function ladderRank(ladder, scaledPaid) {
  let k = 0;
  while (k < ladder.length && ladder[k] > scaledPaid) k += 1;
  return k;
}

/**
 * One pick's benchmark. Deliberately the same source order, the same $3 tail
 * rule ahead of the ladder, the same clamp and the same rounding as
 * computeReport — see the header.
 */
function gradePick(entry, cell, scale) {
  const { players: table, slotLadder } = cell;
  const paid = Number.isFinite(entry.price) ? entry.price : null;
  if (paid === null) {
    return {
      player: entry.player,
      pos: entry.pos,
      team: entry.team || null,
      sleeperId: entry.sleeperId || null,
      slot: entry.slot,
      paid: null,
      bench: null,
      surplus: 0,
      source: "unpriced",
    };
  }
  const scaledPaid = paid * scale;
  let bench = null;
  let source;
  const known = LADDER_POS.has(entry.pos) ? table[entry.player] : undefined;
  if (known && known.pos === entry.pos) {
    bench = known.median;
    source = "player";
  } else if (!LADDER_POS.has(entry.pos)) {
    source = "kdef";
  } else if (scaledPaid <= 3) {
    source = "tail";
  } else {
    const ladder = slotLadder[entry.pos] || [];
    if (ladder.length === 0) {
      source = "tail";
    } else {
      bench = ladder[Math.min(ladderRank(ladder, scaledPaid), ladder.length - 1)];
      source = "slot";
    }
  }
  const scaledSurplus = bench === null ? 0 : bench - scaledPaid;
  return {
    player: entry.player,
    pos: entry.pos,
    // Identity fields ride along untouched: the report draws headshots from
    // them, and re-deriving a sleeper id from a name at render time would be a
    // second, weaker copy of the lookup the entry form already did.
    team: entry.team || null,
    sleeperId: entry.sleeperId || null,
    slot: entry.slot,
    paid,
    bench: bench === null ? null : round1(bench / scale),
    surplus: round1(scaledSurplus / scale),
    source,
  };
}


// ---------------------------------------------------------------------------
// Position rank, and the roster split the report renders as group cards.

/**
 * Every projected player at every position, ranked under ONE league's lens.
 *
 * The rank is the honest form of "how good is this guy": QB7 means seventh
 * best projected QB under YOUR scoring, not under some house default, which is
 * why it cannot be precomputed into an artifact. One pass over the projection
 * index per lens, memoised on the lens object, because the report asks for a
 * rank once per roster row and the board is the same board every time.
 */
let RANK_CACHE = null;
let RANK_LENS = null;

export function positionRanks(lens) {
  if (RANK_CACHE && RANK_LENS === lens) return RANK_CACHE;
  const byGroup = new Map();
  for (const [key, row] of projectionIndex().entries()) {
    const group = key.slice(key.lastIndexOf("|") + 1);
    const games = Number(row.games) || 0;
    if (games <= 0) continue;
    const pts = scoreFromStats(row.stats, group, lens, games);
    if (!Number.isFinite(pts)) continue;
    if (!byGroup.has(group)) byGroup.set(group, []);
    byGroup.get(group).push([key, pts]);
  }
  // K and DEF rank off their own table and are the same under every lens, which
  // is exactly what their entry in kdProjections2026.js says. DEF7 and K12 are
  // still the honest form of "how good is this guy", so the row gets one.
  for (const [key, row] of kdIndex().entries()) {
    const group = key.slice(key.lastIndexOf("|") + 1);
    if (!byGroup.has(group)) byGroup.set(group, []);
    byGroup.get(group).push([key, Number(row.points) || 0]);
  }
  const ranks = new Map();
  for (const rows of byGroup.values()) {
    rows.sort((a, b) => b[1] - a[1]).forEach(([key], i) => ranks.set(key, i + 1));
  }
  RANK_CACHE = ranks;
  RANK_LENS = lens;
  return ranks;
}

/** This player's rank on his own position board, or null when unprojected. */
export function posRankOf(name, pos, ranks) {
  const group = projGroup(pos);
  if (!group || !ranks) return null;
  return ranks.get(`${normName(name)}|${group}`) || null;
}

/**
 * The slot -> card mapping and the card order moved to rateProjection.js in
 * round 6, because the LEAGUE report draws the same cards and a second copy of
 * "which card does a SUPER_FLEX starter land on" is a second answer waiting to
 * disagree. Re-exported so existing callers keep their import path.
 */
export { GROUP_ORDER };

/**
 * The roster as the report draws it: one card per position group for the
 * starters the LINEUP FILLER chose, then one per group for the bench.
 *
 * Starters come from the filler rather than from the slot the user typed into,
 * so a QB entered on the bench who ends up starting in SUPER_FLEX appears
 * where he actually plays. That is also why FLEX is its own card and not
 * folded into the position cards: a flex starter is a flex starter.
 *
 * 🔴 FLEX AND SFLEX ARE GRADED CARDS LIKE ANY OTHER (Aug 30). Ben: "fix the
 * flex to be graded. It's arguably the most important position to win in
 * fantasy football." rateTeamDists now carries a FLEX cell in both formats and
 * an SFLEX cell on the superflex side, measured by flexScore over the corpus,
 * so the badge on a flex card is the same kind of number as the badge on an RB
 * card: a percentile against real teams with its cell named underneath. The
 * two are separate cards and separate cells on purpose — a superflex slot holds
 * a quarterback and a flex slot does not, so pooling them would grade a QB2
 * against a WR4.
 *
 * A card whose cell is missing (SFLEX in a 1QB league, which cannot happen, or
 * any future format the corpus has not measured) still shows its players and no
 * grade: percentileOf returns null on a missing array, which is the pre-Aug-30
 * behaviour preserved as the fallback rather than as the rule.
 *
 * 🔴 A BENCH IS GRADED AGAINST BENCHES (Aug 30, round 3). It used to be scored
 * against the STARTER cell - sums of real teams' best players at a position -
 * so every bench that has ever existed trailed almost all of it. Ben's RB23 +
 * RB30 bench read "trails all 324", which is true of every bench and therefore
 * says nothing about his.
 *
 * The bench cell (dist.benchPos) is one entry per corpus PLAYER the corpus
 * lineup filler did not start, at his reference-lens PPG, and a bench group
 * scores as the MEAN reference PPG of its own projected players. Two
 * consequences, both wanted:
 *   - per player rather than per team, so a two-man bench and a four-man bench
 *     are the same kind of number and team-size K never enters,
 *   - a MEAN rather than a sum, so adding a fourth body cannot raise the badge
 *     by itself. The card answers "how good is the average man on this bench",
 *     which is what a reader thinks it is answering.
 *
 * An unprojected bench player is SHOWN IN THE ROWS AND LEFT OUT OF THE MEAN: a
 * gap in our projection set must not drag a bench toward zero. A group with
 * nothing projected gets no badge and no percentile line at all.
 */
export function buildGroups(picks, proj, projRef, dist, teams) {
  // Membership comes from the shared splitter, so this page and the league
  // report cannot disagree about which card a player appears on. What is left
  // here is the GRADING, which is the half the two pages genuinely differ on.
  const { starterRows, benchRows } = splitRoster(picks, proj);
  // Reference ppg by name+pos, for every score on every card. The ROWS come
  // from the display lineup; the NUMBERS a card is graded on come from here.
  // See the module header for why the two are allowed to disagree.
  const refPpg = (name, pos) => {
    const hit = projRef && projRef.perPlayer ? projRef.perPlayer[playerKey(name, pos)] : null;
    return hit ? hit.ppg : null;
  };

  const starterGroups = [];
  for (const group of GROUP_ORDER) {
    const rows = starterRows.get(group) || [];
    if (rows.length === 0) continue;
    // QB/RB/WR/TE badges reuse the very statistic the chips report, so a card
    // and a chip can never disagree about the same roster. FLEX and SFLEX have
    // no chip (they are lineup constructs, not position rooms) and take
    // flexScore instead — a different statistic against a different cell, but
    // the same "starters plus one depth piece" shape.
    //
    // 🔴 SCORED ON THE REFERENCE PROJECTION, ALWAYS. The card's rows are the
    // user's lineup under the user's scoring; its badge is a percentile, and a
    // percentile has to be computed the way the corpus was.
    let scored = null;
    if (projRef && FLEX_KINDS[group]) scored = flexScore(projRef, projRef.slotCounts, group);
    else if (projRef && GRADE_POSITIONS.includes(group)) {
      scored = positionScore(projRef.matched, group, projRef.slotCounts);
    }
    // The SAME cell the chip uses, so a card badge and a chip cannot disagree
    // about one roster — the invariant this loop was written around. Both are
    // this league's own size or nothing; neither falls back to the pool.
    const posCell = resolvePosCell(dist, teams, group);
    const percentile = scored && scored.matched > 0 && posCell
      ? percentileOf(posCell, scored.score)
      : null;
    starterGroups.push({
      key: group,
      kind: "starters",
      ungraded: KD_POSITIONS.includes(group),
      rows,
      score: scored ? scored.score : null,
      percentile,
      grade: percentile === null ? null : gradeFromPercentile(percentile),
      n: percentile === null ? null : dist.meta.nPos[group],
    });
  }

  // ---- bench: everything the filler did not start, on its own position card
  const benchGroups = [];
  // GROUP_ORDER carries DEF and K itself now; it used to have them appended
  // here because the starter list did not know about them.
  for (const group of GROUP_ORDER) {
    const rows = benchRows.get(group);
    // A position with no bench players simply has no card. Never an empty one.
    if (!rows || rows.length === 0) continue;
    let percentile = null;
    let score = null;
    let scored = 0;
    if (GRADE_POSITIONS.includes(group)) {
      // Membership is the DISPLAY bench (these are the players he is not
      // starting); the points are the reference lens's, because the cell
      // underneath was built that way.
      const ppgs = rows
        .map((r) => refPpg(r.player, r.pos))
        .filter((v) => Number.isFinite(v));
      scored = ppgs.length;
      if (scored > 0) {
        score = ppgs.reduce((a, b) => a + b, 0) / scored;
        percentile = percentileOf(dist.benchPos ? dist.benchPos[group] : null, score);
      }
    }
    benchGroups.push({
      key: group,
      kind: "bench",
      // No corpus cell exists for these two and none is planned; the card says
      // so where a badge would be, rather than reading as "not projected".
      ungraded: KD_POSITIONS.includes(group),
      rows,
      score,
      // How many of the rows the mean is actually built from, so the copy can
      // say "2 of 3 graded" rather than implying it measured everybody.
      scored,
      percentile,
      grade: percentile === null ? null : gradeFromPercentile(percentile),
      n: percentile === null || !dist.meta.nBenchPos ? null : dist.meta.nBenchPos[group],
    });
  }

  return { starters: starterGroups, bench: benchGroups };
}

/**
 * Grade one hand-entered team.
 *
 * @param settings { budget, teams, positions, scoring } — house shape
 *   throughout; a saved league from useLeagueSettings drops straight in with no
 *   mapping, which is the whole reason the picker reads that key.
 * @param entries  [{ player, pos, slot, price }] — price null/absent allowed
 * @returns the whole report object TeamReportView renders. Never throws on a
 *   half-filled roster: an empty ladder grades as an empty report.
 */
export function rateTeam({ settings, entries }) {
  const positions = (settings && settings.positions) || {};
  const budget = Number(settings && settings.budget) || SCALE_BUDGET;
  const teams = Number(settings && settings.teams) || 0;
  const fmt = pickFormat(positions);
  const cell = benchmarks[fmt.key];
  const dist = dists[fmt.key];
  const scale = SCALE_BUDGET / budget;

  const filled = (entries || []).filter((e) => e && e.player && e.pos);

  // PRICES ARE ALL-OR-NOTHING AT THE GRADED POSITIONS. A partly priced roster
  // would sum real surpluses against zeros and read as a mediocre team rather
  // than an incomplete entry. K and DEF are exempt because they grade as 0 by
  // rule anyway, so forgetting a $1 kicker must not cost the user their grade.
  const needPrice = filled.filter((e) => LADDER_POS.has(e.pos));
  const pricedCount = needPrice.filter((e) => Number.isFinite(e.price) && e.price > 0).length;
  const hasPrices = needPrice.length > 0 && pricedCount === needPrice.length;

  const picks = filled.map((e) =>
    gradePick({ ...e, price: hasPrices ? e.price : null }, cell, scale)
  );

  const spent = picks.reduce((a, p) => a + (p.paid || 0), 0);
  const surplus = hasPrices ? round1(picks.reduce((a, p) => a + p.surplus, 0)) : null;
  const surplusScaled = surplus === null ? null : surplus * scale;
  // The market grade's population: this league's own size or nothing. See
  // resolveSurplusCell — a pooled surplus percentile is a league-size reading
  // wearing a roster's clothes, so its absence is reported, not papered over.
  const surplusCell = resolveSurplusCell(dist, teams);
  const surplusPercentile =
    surplusScaled === null || surplusCell === null
      ? null
      : percentileOf(surplusCell.values, surplusScaled);
  const grade = surplusPercentile === null ? null : gradeFromPercentile(surplusPercentile);

  // A pick a benchmark actually covers — the only kind the headlines may name.
  const gradeable = picks.filter((p) => p.source === "player" || p.source === "slot");
  const bestBuy = gradeable.length
    ? gradeable.reduce((a, p) => (p.surplus > a.surplus ? p : a))
    : null;
  const worst = gradeable.length
    ? gradeable.reduce((a, p) => (p.surplus < a.surplus ? p : a))
    : null;

  // Projections, TWICE. See the module header: display is the user's scoring
  // and is everything visible; reference is the corpus's lens and is every
  // percentile. The reference pass never needs the user's scoring, so it runs
  // even for a caller that supplied none.
  const slots = projectionSlots(positions);
  const lens = (settings && settings.scoring) || null;
  const proj = lens ? { ...projectTeam(filled, slots, lens), slotCounts: slots } : null;
  const projRef = { ...projectTeam(filled, slots, REFERENCE_LENS), slotCounts: slots };
  // The week1 comparison population: this league's own size bucket when the
  // corpus has a real one, else the pooled cell. The meter draws THIS array,
  // so the marker and the percentile can never come from different pictures.
  const week1Cell = resolveWeek1Cell(dist, teams, projectedSlotCount(slots));
  const week1Percentile = week1Cell && projRef.starterMatchedCount > 0
    ? percentileOf(week1Cell.values, projRef.week1)
    : null;
  // The season total's own population. Same reference lens, same gate, and the
  // same absent-not-pooled rule as every other cell on this report.
  const seasonCell = resolveSeasonCell(dist, teams);
  const seasonPercentile = seasonCell && projRef.starterMatchedCount > 0
    ? percentileOf(seasonCell.values, projRef.season)
    : null;

  const positionChips = {};
  for (const group of GRADE_POSITIONS) {
    const s = positionScore(projRef.matched, group, slots);
    // This league's own size or nothing. A pooled per-position array reads a
    // 10-team room as strong at every position, which is the room's size
    // arriving as six separate compliments about the roster.
    const posCell = resolvePosCell(dist, teams, group);
    // Nothing matched at a position is a gap in our projection set, not a thin
    // roster — the chip says "no projection", exactly as the room report does.
    positionChips[group] = {
      ...s,
      word: s.matched > 0 && posCell ? positionWord(s.score, posCell) : null,
      percentile: s.matched > 0 && posCell ? percentileOf(posCell, s.score) : null,
    };
  }

  // 🔴 WHETHER THE CORPUS CAN SAY ANYTHING AT ALL ABOUT A LEAGUE THIS SIZE.
  // Superflex 14 is the live case: 42 teams, under the artifact's floor, so no
  // surplus cell and no position cells ship, and every corpus-relative number
  // on the page is null at once. The report says that once, plainly, rather
  // than rendering six blank chips and an empty grade badge and leaving the
  // reader to infer it. week1 IS part of this test: since the pooled level was
  // removed it answers the same way as the other two, so at an uncovered size
  // every corpus number on the page — grade, chips and projection percentile —
  // is null together and one card covers all of it. The 8- and 16-team leagues
  // that were quietly being placed against the pooled cell land here too.
  const posGroupsAtSize = Object.keys((dist.posByTeams || {})[String(teams)] || {}).length;
  const corpusAtSize = {
    teams,
    week1: week1Cell !== null,
    // Pinned to week1 in the artifact (the generator asserts the two maps cover
    // the same sizes with the same counts), so this never disagrees with the
    // line above — it is carried so a reader of this object does not have to
    // know that pin exists.
    season: seasonCell !== null,
    surplus: surplusCell !== null,
    positions: posGroupsAtSize,
    any: week1Cell !== null || surplusCell !== null || posGroupsAtSize > 0,
  };

  return {
    format: fmt.label,
    benchmarkFormat: fmt.key,
    teams,
    budget,
    positions,
    isSflex: (Number(positions.SFLEX) || 0) > 0,
    qbSlots: Number(positions.QB) || 0,
    benchmarkLabel: BENCHMARK_LABEL,
    nRooms: cell.meta.nRooms,
    benchmarkDate: cell.meta.date,
    corpus: dist.meta,
    // The two arrays the report's distribution strips draw. By reference, not
    // copied, and resolved HERE rather than in the view: pickFormat is the one
    // place a format becomes a cell, and a component re-deriving that key could
    // draw one cell's bars under another cell's counts.
    corpusCurves: {
      week1: week1Cell ? week1Cell.values : null,
      surplus: surplusCell ? surplusCell.values : null,
    },
    hasPrices,
    pricedCount,
    priceableCount: needPrice.length,
    picks,
    spent,
    surplus,
    surplusScaled,
    surplusPercentile,
    // Null when the corpus has no cell for this league size, which the view
    // must say out loud rather than fall back to a pooled grade.
    surplusCell,
    corpusAtSize,
    grade,
    bestBuy: bestBuy && bestBuy.surplus > 0 ? bestBuy : null,
    worstOverpay: worst && worst.surplus < 0 ? worst : null,
    projection: proj,
    // The comparison team, kept on the report so a test can prove the two
    // halves are different numbers rather than take it on trust.
    projectionRef: projRef,
    week1: proj ? proj.week1 : null,
    week1Ref: projRef.week1,
    week1Cell,
    week1Percentile,
    // 🔴 THE SEASON TOTAL IS THE SAME LINEUP, READ THE OTHER WAY. It sums the
    // season points of the very starters week1 sums the per-game points of
    // (optimalLineup's header carries why the lineup is not re-optimised), so
    // the display half is the user's own scoring and the percentile half is the
    // reference lens — exactly the split week1 already runs on.
    season: proj ? proj.season : null,
    seasonRef: projRef.season,
    seasonCell,
    seasonPercentile,
    // The lens each half was computed under, said out loud. corpus.lens is the
    // artifact's own record of how its arrays were scored, so the face line
    // cannot claim a lens the data was not built with.
    comparisonLens: dist.meta.lens,
    positionChips,
    groups: buildGroups(picks, proj, projRef, dist, teams),
    posRanks: lens ? positionRanks(lens) : null,
  };
}
